import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { LocationDto, WarehouseDto } from '@/types/organization';
import type { MaterialDto, PagedResult } from '@/types/materials';
import type { BomDto } from '@/types/manufacturing';
import type { CreateWeightProductionOrderRequest, WeightProductionOrderDto, WeightProductionOrderLineInput } from '@/types/weightProduction';

// Weight is the default because that is how every line behaved before the basis was selectable —
// an existing plan must not change meaning just because the field appeared.
const EMPTY_LINE: WeightProductionOrderLineInput = { materialId: '', plannedQty: 0, planBasis: 'Weight', batchNo: '' };

export default function WeightProductionOrdersPage() {
  const { t } = useTranslation('weightProduction');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();
  const { data: config } = useProcessConfiguration();
  // A company picks one shape company-wide (see ProcessConfiguration.WeightProductionRawMaterialHandlingEnabled)
  // rather than choosing per plan — on, only BOM Based is offered; off, only Direct.
  const rawMaterialHandlingEnabled = config?.weightProductionRawMaterialHandlingEnabled ?? true;

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['weight-production-orders'],
    queryFn: async () => (await apiClient.get<WeightProductionOrderDto[]>('/weight-production-orders')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  // Only items with Weight-Based Production configured on the Item Master — mirrors
  // WeightProductionOrderService.BuildLinesAsync's own server-side check.
  // Producible items only, on BOTH tabs: finished goods and semi-finished goods. A plan states what
  // is being PRODUCED, and an SFG is as much a production output as an FG — it is simply consumed
  // again further down the line. Raw materials stay out; they are what gets consumed, never made.
  // Weigh & Label configuration is also required, since the kiosk validates every weighing against
  // the item's own min/max tolerance and could not accept an item that has none.
  // The BOM tab narrows this further to items that actually have an Active BOM.
  const materials = (materialsPage?.items ?? []).filter(
    (m) => m.weightBasedProductionEnabled && (m.materialType === 'FG' || m.materialType === 'SFG'),
  );

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Two ways to build a plan. BOM mode names one output item and explodes its Active BOM into
  // materials that must be issued from stock first; Direct just lists what will be weighed.
  const [sourceMode, setSourceMode] = useState<'Bom' | 'Direct'>('Direct');
  const [outputMaterialId, setOutputMaterialId] = useState('');
  const [outputPlannedQty, setOutputPlannedQty] = useState<number>(0);
  const [outputPlanBasis, setOutputPlanBasis] = useState<'Weight' | 'Quantity'>('Weight');
  const [outputBatchNo, setOutputBatchNo] = useState('');

  // Only items that actually HAVE an Active BOM can be planned in BOM mode — otherwise the operator
  // picks an item and only discovers at Save that there is nothing to explode.
  const { data: boms = [] } = useQuery({
    queryKey: ['boms'],
    queryFn: async () => (await apiClient.get<BomDto[]>('/boms')).data,
  });
  const bomOutputs = useMemo(() => {
    const activeBoms = boms.filter((b) => b.status === 'Active');
    return activeBoms
      .map((b) => ({ bom: b, material: materials.find((m) => m.id === b.materialId) }))
      .filter((x) => x.material !== undefined);
  }, [boms, materials]);
  const selectedBom = bomOutputs.find((x) => x.material!.id === outputMaterialId)?.bom;
  const [warehouseId, setWarehouseId] = useState('');
  // Where every weighing against this plan posts its produced stock — one choice for the whole
  // plan, made once at creation, not per line. Warehouse-scoped, so it resets whenever the
  // warehouse changes rather than silently pointing at a location outside it.
  const [zoneId, setZoneId] = useState('');
  const [plannedStartDate, setPlannedStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<WeightProductionOrderLineInput[]>([{ ...EMPTY_LINE }]);

  const { data: zoneLocations = [] } = useQuery({
    queryKey: ['locations', warehouseId],
    queryFn: async () => (await apiClient.get<LocationDto[]>('/locations', { params: { warehouseId } })).data,
    enabled: open && Boolean(warehouseId),
  });

  const resetForm = () => {
    setEditingId(null);
    setSourceMode(rawMaterialHandlingEnabled ? 'Bom' : 'Direct');
    setOutputMaterialId('');
    setOutputPlannedQty(0);
    setOutputPlanBasis('Weight');
    setOutputBatchNo('');
    setWarehouseId('');
    setZoneId('');
    setPlannedStartDate('');
    setPlannedEndDate('');
    setRemarks('');
    setLines([{ ...EMPTY_LINE }]);
  };

  const openEdit = (r: WeightProductionOrderDto) => {
    setEditingId(r.id);
    // The plan's own recorded mode, not the company's current toggle — a plan already made one way
    // must still show correctly if the toggle is switched after it was created.
    setSourceMode(r.sourceMode);
    setWarehouseId(r.warehouseId);
    setZoneId(r.locationId ?? '');
    setPlannedStartDate(r.plannedStartDate ? r.plannedStartDate.slice(0, 10) : '');
    setPlannedEndDate(r.plannedEndDate ? r.plannedEndDate.slice(0, 10) : '');
    setRemarks(r.remarks ?? '');
    setLines(r.lines.map((l) => ({ materialId: l.materialId, planBasis: l.planBasis, plannedQty: l.plannedQty, locationId: l.locationId, batchId: l.batchId, batchNo: l.batchNo })));
    setOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['weight-production-orders'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId && l.plannedQty > 0);
      const payload: CreateWeightProductionOrderRequest = {
        warehouseId,
        locationId: zoneId,
        plannedStartDate: plannedStartDate || null,
        plannedEndDate: plannedEndDate || null,
        remarks: remarks || null,
        // BOM mode sends no lines — the server explodes the output item's Active BOM instead, the
        // same division of labour Job Work uses.
        lines: sourceMode === 'Bom' ? [] : cleanLines,
        sourceMode,
        outputMaterialId: sourceMode === 'Bom' ? outputMaterialId : null,
        outputPlannedQty: sourceMode === 'Bom' ? outputPlannedQty : null,
        outputPlanBasis: sourceMode === 'Bom' ? outputPlanBasis : null,
        outputBatchNo: sourceMode === 'Bom' ? outputBatchNo || null : null,
      };
      return editingId ? apiClient.put(`/weight-production-orders/${editingId}`, payload) : apiClient.post('/weight-production-orders', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/weight-production-orders/${id}/release`),
    onSuccess: invalidate,
  });
  const cancelMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/weight-production-orders/${id}/cancel`),
    onSuccess: invalidate,
  });

  // Short-close: ending a plan that will never meet its quantity. The reason is mandatory because
  // this is the record of WHY production stopped early — without it the shortfall is unexplainable
  // months later.
  const [closing, setClosing] = useState<WeightProductionOrderDto | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const closeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/weight-production-orders/${id}/close`, { reason: closeReason }),
    onSuccess: () => { invalidate(); setClosing(null); setCloseReason(''); },
  });

  const columns: DataTableColumn<WeightProductionOrderDto>[] = [
    { field: 'orderNumber', headerName: t('orders.columns.orderNumber') },
    { field: 'warehouseName', headerName: t('orders.columns.warehouse'), filterable: true },
    { field: 'plannedStartDate', headerName: t('orders.columns.plannedStart'), render: (r) => (r.plannedStartDate ? formatDate(r.plannedStartDate) : '—') },
    { field: 'plannedEndDate', headerName: t('orders.columns.plannedEnd'), render: (r) => (r.plannedEndDate ? formatDate(r.plannedEndDate) : '—') },
    { field: 'planned', headerName: t('orders.columns.planned'), render: (r) => r.lines.reduce((sum, l) => sum + l.plannedQty, 0) },
    { field: 'produced', headerName: t('orders.columns.produced'), render: (r) => r.lines.reduce((sum, l) => sum + l.producedQty, 0) },
    { field: 'remaining', headerName: t('orders.columns.remaining'), render: (r) => r.lines.reduce((sum, l) => sum + l.remainingQty, 0) },
    { field: 'status', headerName: t('orders.columns.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_MANAGE}>
          <Stack direction="row" spacing={0.5}>
            {r.status === 'Draft' && (
              <>
                <Button size="small" onClick={() => openEdit(r)}>{t('orders.dialog.title')}</Button>
                <Button size="small" disabled={releaseMutation.isPending} onClick={() => releaseMutation.mutate(r.id)}>
                  {t('orders.release')}
                </Button>
              </>
            )}
            {/* Only offered once something has been produced — closing a plan that made nothing is
                a cancellation, not a short-close. */}
            {(r.status === 'InProduction' || r.status === 'PartiallyCompleted') && (
              <Button size="small" onClick={() => { setClosing(r); setCloseReason(''); }}>
                {t('orders.close')}
              </Button>
            )}
            {r.status !== 'Completed' && r.status !== 'Cancelled' && r.status !== 'Closed' && (
              <Button size="small" color="warning" disabled={cancelMutation.isPending} onClick={() => cancelMutation.mutate(r.id)}>
                {t('orders.cancel')}
              </Button>
            )}
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('orders.title')}
        subtitle={t('orders.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.WEIGHT_PRODUCTION_MANAGE}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetForm(); setOpen(true); }}>
              {t('orders.addNew')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={orders} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="weight-production-orders" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{t('orders.dialog.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <SearchableSelect
              label={t('orders.dialog.warehouse')} value={warehouseId} fullWidth
              onChange={(v) => { setWarehouseId(v); setZoneId(''); }}
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />
            {/* Where every weighing against this plan posts its produced stock — required, one
                choice for the whole plan. Warehouse-scoped: empty and disabled until a warehouse is
                picked, since a zone belonging to a different warehouse would be meaningless here. */}
            <SearchableSelect
              label={t('orders.dialog.zone')} value={zoneId} onChange={setZoneId} fullWidth
              disabled={!warehouseId}
              helperText={!warehouseId ? t('orders.dialog.zoneNeedsWarehouse') : undefined}
              options={zoneLocations.map((l) => ({ value: l.id, label: l.zoneName ? `${l.zoneName} / ${l.code}` : l.code }))}
            />
            <TextField
              type="date" label={t('orders.dialog.plannedStartDate')} value={plannedStartDate} fullWidth
              slotProps={{ inputLabel: { shrink: true } }} onChange={(e) => setPlannedStartDate(e.target.value)}
            />
            <TextField
              type="date" label={t('orders.dialog.plannedEndDate')} value={plannedEndDate} fullWidth
              slotProps={{ inputLabel: { shrink: true } }} onChange={(e) => setPlannedEndDate(e.target.value)}
            />
            <TextField label={t('orders.dialog.remarks')} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />

            {/* How the plan is built — BOM mode consumes stock before it can produce, Direct doesn't.
                A company-wide choice (ProcessConfiguration.WeightProductionRawMaterialHandlingEnabled),
                not a per-plan one, so there is nothing to switch between here: a new plan is always
                built the way the company has configured, and an existing plan always shows the mode
                it actually was — see openEdit/resetForm. */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {sourceMode === 'Bom' ? t('orders.dialog.tabBom') : t('orders.dialog.tabDirect')}
              </Typography>
            </Box>

            {sourceMode === 'Bom' ? (
              <Stack spacing={2}>
                <Alert severity="info">{t('orders.dialog.bomExplain')}</Alert>
                <SearchableSelect
                  label={t('orders.dialog.outputMaterial')} value={outputMaterialId} onChange={setOutputMaterialId} fullWidth
                  helperText={bomOutputs.length === 0 ? t('orders.dialog.noActiveBoms') : t('orders.dialog.outputMaterialHint')}
                  options={bomOutputs.map((x) => ({ value: x.material!.id, label: `${x.material!.code} — ${x.material!.name} (${x.bom.bomNumber})` }))}
                />
                <Stack direction="row" spacing={1}>
                  <TextField
                    select label={t('orders.dialog.planBasis')} value={outputPlanBasis} size="small" sx={{ minWidth: 130 }}
                    onChange={(e) => setOutputPlanBasis(e.target.value as 'Weight' | 'Quantity')}
                  >
                    <MenuItem value="Weight">{t('orders.dialog.basisWeight')}</MenuItem>
                    <MenuItem value="Quantity">{t('orders.dialog.basisQuantity')}</MenuItem>
                  </TextField>
                  <TextField
                    type="number" size="small" sx={{ minWidth: 160 }}
                    label={t('orders.dialog.plannedQtyIn', {
                      unit: outputPlanBasis === 'Quantity'
                        ? t('orders.dialog.unitPcs')
                        : materials.find((m) => m.id === outputMaterialId)?.weightUom ?? 'KG',
                    })}
                    value={outputPlannedQty}
                    onChange={(e) => setOutputPlannedQty(Number(e.target.value))}
                  />
                  {/* Free-typed, same as a Direct line — the server finds-or-creates the batch by
                      material + batch number, so a brand-new lot number is normal, not an error. */}
                  <TextField
                    label={t('orders.dialog.batchNo')} size="small" sx={{ minWidth: 160 }}
                    value={outputBatchNo}
                    onChange={(e) => setOutputBatchNo(e.target.value)}
                  />
                </Stack>

                {/* The explosion made visible before Save — the same arithmetic the server performs,
                    scaled against the BOM's own base quantity. */}
                {selectedBom && outputPlannedQty > 0 && (
                  <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                      {t('orders.dialog.willConsume', { bom: selectedBom.bomNumber })}
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {selectedBom.lines.map((l) => (
                        <Stack key={l.id} direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">{`${l.materialCode} — ${l.materialName}`}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {Math.round((l.qty * outputPlannedQty / (selectedBom.baseQty || 1)) * 1000) / 1000}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            ) : (
              <>
            <Typography variant="subtitle2">{t('orders.dialog.lines')}</Typography>
            <Typography variant="caption" color="text.secondary">{t('orders.dialog.notConfiguredHint')}</Typography>
            {lines.map((line, idx) => {
              const lineMaterial = materials.find((m) => m.id === line.materialId);
              const planUnit = line.planBasis === 'Quantity' ? t('orders.dialog.unitPcs') : lineMaterial?.weightUom ?? 'KG';
              return (
              <Stack key={idx} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <SearchableSelect
                  label={t('orders.dialog.material')} value={line.materialId} size="small" sx={{ minWidth: 280, flex: '2 1 280px' }}
                  onChange={(value) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId: value } : l)))}
                  options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                />
                {/* Makes explicit what "20" means on this line — planning 20 kg of bulk and planning
                    20 discrete packs are both legitimate, and the number alone can't say which. */}
                <TextField
                  select label={t('orders.dialog.planBasis')} value={line.planBasis ?? 'Weight'} size="small" sx={{ minWidth: 130 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, planBasis: e.target.value as 'Weight' | 'Quantity' } : l)))}
                >
                  <MenuItem value="Weight">{t('orders.dialog.basisWeight')}</MenuItem>
                  <MenuItem value="Quantity">{t('orders.dialog.basisQuantity')}</MenuItem>
                </TextField>
                <TextField
                  type="number" label={t('orders.dialog.plannedQtyIn', { unit: planUnit })} value={line.plannedQty} size="small" sx={{ minWidth: 140 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, plannedQty: Number(e.target.value) } : l)))}
                />
                {/* Free-typed — a batch that doesn't exist yet (a new lot) is normal, not an error;
                    the server finds-or-creates it by material + batch number on save. */}
                <TextField
                  label={t('orders.dialog.batchNo')} value={line.batchNo ?? ''} size="small" sx={{ minWidth: 140 }}
                  onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, batchNo: e.target.value } : l)))}
                />
                <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
              );
            })}
            <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])} sx={{ alignSelf: 'flex-start' }}>
              {t('orders.dialog.addLine')}
            </Button>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('orders.cancel')}</Button>
          <Button variant="contained" disabled={!warehouseId || !zoneId || saveMutation.isPending} onClick={() => saveMutation.mutate()}>
            {t('orders.dialog.title')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(closing)} onClose={() => setClosing(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('orders.closeDialog.title', { plan: closing?.orderNumber ?? '' })}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>{t('orders.closeDialog.explain')}</Alert>
          {closing && (
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              {closing.lines.map((l) => (
                <Stack key={l.id} direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">{l.materialCode}</Typography>
                  <Typography variant="body2">
                    {t('orders.closeDialog.shortfall', { produced: l.producedQty, planned: l.plannedQty, short: Math.max(0, l.plannedQty - l.producedQty) })}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          )}
          <TextField
            label={t('orders.closeDialog.reason')} fullWidth required autoFocus multiline minRows={2}
            value={closeReason} onChange={(e) => setCloseReason(e.target.value)}
            helperText={t('orders.closeDialog.reasonHelper')}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setClosing(null)}>{t('orders.cancel')}</Button>
          <Button
            variant="contained" color="warning"
            disabled={!closeReason.trim() || closeMutation.isPending}
            onClick={() => closing && closeMutation.mutate(closing.id)}
          >
            {t('orders.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
